"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Eye, Heart, Copy, Check, ArrowUpRight } from "lucide-react";
import type { EssenceVersion } from "@/lib/db/schema";
import type { EssenceDetail as EssenceDetailType } from "@/hooks/use-essence-detail";
import { tintStyle } from "@/lib/essences/tint";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { EssenceTitle } from "./essence-title";
import { FavoriteButton } from "./favorite-button";
import { VersionSelector, HistoricalNotice } from "./version-selector";
import { CommunityTagsSection } from "./community-tags-section";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";

/* ----------------------------------------------------------------------------
 * Source shapes
 * ------------------------------------------------------------------------- */

export interface EntryShape {
  text: string;
  subeffects?: EntryShape[];
}

export interface SourceShape {
  effects?: EntryShape[];
  sections?: { heading: string | null; type: string; entries: EntryShape[] }[];
  search?: { categories?: string[]; traits?: string[]; keywords?: string[] };
}

export function getSource(version: EssenceVersion): SourceShape {
  return (version.sourceData ?? {}) as SourceShape;
}

/* ----------------------------------------------------------------------------
 * View tracking
 * ------------------------------------------------------------------------- */

function getSessionFingerprint(): string {
  if (typeof window === "undefined") return "";
  let fp = sessionStorage.getItem("essence_fp");
  if (!fp) {
    fp = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("essence_fp", fp);
  }
  return fp;
}

export function useTrackView(essenceId: string) {
  useEffect(() => {
    const fingerprint = getSessionFingerprint();
    fetch("/api/views", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ essenceId, sessionFingerprint: fingerprint }),
    }).catch(() => {});
  }, [essenceId]);
}

/* ----------------------------------------------------------------------------
 * Pieces
 * ------------------------------------------------------------------------- */

export function EssenceHeading({
  version,
  size,
  as = "h1",
  className,
}: {
  version: Pick<EssenceVersion, "title" | "author">;
  size: "modal" | "page" | "compact";
  as?: "h1" | "h2" | "span";
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)} style={tintStyle(version.author)}>
      <EssenceTitle title={version.title} as={as} size={size} />
      <p
        className={cn(
          "mt-2 flex items-center gap-2 text-ink-2",
          size === "page" ? "text-[15px]" : size === "compact" ? "text-[12.5px]" : "text-[14px]"
        )}
      >
        <span
          aria-hidden
          className="size-1.5 shrink-0 rounded-full"
          style={{
            background: "hsl(var(--tint-h) 80% 72%)",
            boxShadow: "0 0 8px hsl(var(--tint-h) 80% 72% / 0.8)",
          }}
        />
        <span className="text-ink-3">by</span>
        <span className="font-medium text-ink">{version.author}</span>
      </p>
    </div>
  );
}

export function EssenceStats({
  essence,
  className,
}: {
  essence: EssenceDetailType["essence"];
  className?: string;
}) {
  return (
    <dl
      className={cn("flex items-center gap-4 font-mono text-[12px] tabular text-ink-3", className)}
    >
      <div className="flex items-center gap-1.5">
        <dt className="sr-only">Views</dt>
        <Eye className="size-3.5" aria-hidden />
        <dd>
          {essence.viewCount.toLocaleString()} <span className="font-sans">views</span>
        </dd>
      </div>
      <div className="flex items-center gap-1.5">
        <dt className="sr-only">Favorites</dt>
        <Heart className="size-3.5" aria-hidden />
        <dd>
          {essence.favoriteCount.toLocaleString()} <span className="font-sans">favorites</span>
        </dd>
      </div>
    </dl>
  );
}

export function CopyMarkdownButton({
  version,
  source,
  size = "sm",
  className,
}: {
  version: EssenceVersion;
  source: SourceShape;
  size?: "sm" | "md" | "icon-sm";
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    const md = buildEssenceMarkdown(version, source);
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard unavailable; nothing to do.
    }
  }

  if (size === "icon-sm") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleCopy}
            aria-label={copied ? "Copied Markdown" : "Copy as Markdown"}
            className={className}
          >
            {copied ? <Check className="text-success" /> : <Copy />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? "Copied" : "Copy as Markdown"}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Button
      variant="outline"
      size={size}
      onClick={handleCopy}
      className={cn("rounded-full", className)}
    >
      {copied ? <Check className="text-success" /> : <Copy />}
      {copied ? "Copied" : "Copy Markdown"}
    </Button>
  );
}

export function FullPageLink({
  essenceId,
  query,
  className,
}: {
  essenceId: string;
  query?: string;
  className?: string;
}) {
  const href = query ? `/essences/${essenceId}?${query}` : `/essences/${essenceId}`;
  return (
    <Button variant="outline" size="sm" asChild className={cn("rounded-full", className)}>
      <Link href={href}>
        Open full page
        <ArrowUpRight />
      </Link>
    </Button>
  );
}

/* ----------------------------------------------------------------------------
 * Body: summary, description, effects, sections, tags
 * ------------------------------------------------------------------------- */

export function EssenceBody({
  version,
  source,
  className,
}: {
  version: EssenceVersion;
  source: SourceShape;
  className?: string;
}) {
  const tags = uniqueTags(
    source.search?.categories ?? [],
    source.search?.traits ?? [],
    source.search?.keywords ?? []
  );

  return (
    <div className={cn("space-y-9", className)}>
      {version.aiSummary && (
        <section
          aria-labelledby="ai-summary-label"
          className="relative rounded-r-xl border-l-2 border-accent bg-[linear-gradient(90deg,rgb(167_139_250/0.1),transparent_70%)] py-4 pr-4 pl-5"
        >
          <p id="ai-summary-label" className="micro-label text-accent">
            AI summary
          </p>
          <p className="mt-2 text-pretty text-[15.5px] leading-[1.65] text-ink">
            {version.aiSummary}
          </p>
          <p className="mt-2 text-[11.5px] text-ink-4">
            Generated précis — the sections below are the author&apos;s original text.
          </p>
        </section>
      )}

      {version.description && (
        <section aria-labelledby="description-heading">
          <SectionHeading id="description-heading">Description</SectionHeading>
          <p className="reading mt-3 text-pretty whitespace-pre-line">{version.description}</p>
        </section>
      )}

      {source.effects && source.effects.length > 0 && (
        <section aria-labelledby="effects-heading">
          <SectionHeading id="effects-heading" count={countEntries(source.effects)}>
            Effects
          </SectionHeading>
          <EntryList entries={source.effects} depth={0} className="mt-4" />
        </section>
      )}

      {source.sections && source.sections.length > 0 && (
        <div className="space-y-8">
          {source.sections.map((section, i) => {
            const heading = section.heading?.trim() || humanizeType(section.type);
            const showType =
              section.type &&
              section.type !== "other" &&
              heading.toLowerCase() !== section.type.toLowerCase();
            return (
              <section key={i} aria-labelledby={`section-${i}`}>
                <SectionHeading
                  id={`section-${i}`}
                  eyebrow={showType ? humanizeType(section.type) : undefined}
                >
                  {heading}
                </SectionHeading>
                <EntryList entries={section.entries} depth={0} className="mt-4" />
              </section>
            );
          })}
        </div>
      )}

      {tags.length > 0 && (
        <section aria-label="Tags" className="border-t border-line pt-6">
          <TagGroups tags={tags} />
        </section>
      )}
    </div>
  );
}

function SectionHeading({
  id,
  children,
  count,
  eyebrow,
}: {
  id: string;
  children: React.ReactNode;
  count?: number;
  eyebrow?: string;
}) {
  return (
    <div className="flex items-baseline gap-3">
      <h2 id={id} className="font-display text-[17px] font-semibold tracking-[-0.01em] text-ink">
        {eyebrow && <span className="micro-label mb-1 block">{eyebrow}</span>}
        {children}
      </h2>
      {count !== undefined && (
        <span className="font-mono text-[11.5px] tabular text-ink-4">{count}</span>
      )}
      <span aria-hidden className="ml-1 h-px flex-1 self-center bg-line" />
    </div>
  );
}

function EntryList({
  entries,
  depth,
  className,
}: {
  entries: EntryShape[];
  depth: number;
  className?: string;
}) {
  return (
    <ul
      className={cn(
        "space-y-3",
        depth === 0 && "pl-4",
        depth > 0 && "mt-2.5 space-y-2 border-l border-[rgb(167_139_250/0.22)] pl-[18px]",
        className
      )}
    >
      {entries.map((entry, i) => (
        <li key={i} className="relative">
          {depth === 0 && (
            <span
              aria-hidden
              className="absolute top-[0.72em] -left-4 size-1.5 rounded-full bg-accent/70"
            />
          )}
          <p
            className={cn(
              "reading text-pretty",
              depth === 0 ? "pl-0 text-ink-2" : "text-[14.5px] text-ink-2/90"
            )}
          >
            {entry.text}
          </p>
          {entry.subeffects && entry.subeffects.length > 0 && (
            <EntryList entries={entry.subeffects} depth={depth + 1} />
          )}
        </li>
      ))}
    </ul>
  );
}

type TagKind = "category" | "trait" | "keyword";

function uniqueTags(categories: string[], traits: string[], keywords: string[]) {
  const seen = new Set<string>();
  const tags: { value: string; kind: TagKind }[] = [];
  const push = (values: string[], kind: TagKind) => {
    for (const value of values) {
      const key = value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      tags.push({ value, kind });
    }
  };
  push(categories, "category");
  push(traits, "trait");
  push(keywords, "keyword");
  return tags;
}

function TagGroups({ tags }: { tags: { value: string; kind: TagKind }[] }) {
  const groups: { kind: TagKind; label: string }[] = [
    { kind: "category", label: "Categories" },
    { kind: "trait", label: "Traits" },
    { kind: "keyword", label: "Keywords" },
  ];
  return (
    <dl className="space-y-3.5">
      {groups.map((g) => {
        const items = tags.filter((t) => t.kind === g.kind);
        if (items.length === 0) return null;
        return (
          <div key={g.kind} className="grid gap-1.5 sm:grid-cols-[88px_1fr] sm:gap-3">
            <dt className="micro-label pt-1">{g.label}</dt>
            <dd className="flex flex-wrap gap-1.5">
              {items.map((t) => (
                <Badge key={t.value} variant={t.kind}>
                  {t.value}
                </Badge>
              ))}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function countEntries(entries: EntryShape[]): number {
  let n = 0;
  const walk = (items: EntryShape[]) => {
    for (const e of items) {
      n++;
      if (e.subeffects) walk(e.subeffects);
    }
  };
  walk(entries);
  return n;
}

function humanizeType(type: string): string {
  return type.replace(/[-_]/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/* ----------------------------------------------------------------------------
 * Markdown export
 * ------------------------------------------------------------------------- */

export function buildEssenceMarkdown(version: EssenceVersion, source: SourceShape): string {
  const lines: string[] = [];
  lines.push(`# ${version.title}`);
  lines.push("");
  lines.push(`By ${version.author}`);
  lines.push("");

  if (version.description) {
    lines.push(version.description);
    lines.push("");
  }

  if (source.effects && source.effects.length > 0) {
    lines.push("## Effects");
    lines.push("");
    for (const entry of source.effects) {
      appendEntryMarkdown(lines, entry, 0);
    }
    lines.push("");
  }

  for (const section of source.sections ?? []) {
    const heading = section.heading?.trim() || humanizeType(section.type);
    lines.push(`## ${heading}`);
    lines.push("");
    for (const entry of section.entries) {
      appendEntryMarkdown(lines, entry, 0);
    }
    lines.push("");
  }

  return lines.join("\n").trim();
}

function appendEntryMarkdown(lines: string[], entry: EntryShape, depth: number) {
  const indent = "  ".repeat(depth);
  lines.push(`${indent}- ${entry.text}`);
  if (entry.subeffects && entry.subeffects.length > 0) {
    for (const sub of entry.subeffects) {
      appendEntryMarkdown(lines, sub, depth + 1);
    }
  }
}

/* ----------------------------------------------------------------------------
 * Default composition (used inside the modal)
 * ------------------------------------------------------------------------- */

interface EssenceDetailProps {
  data: EssenceDetailType;
  onVersionChange?: (version: number) => void;
  /** Browse query string to preserve on the full-page link. */
  browseQuery?: string;
}

export function EssenceDetail({ data, onVersionChange, browseQuery }: EssenceDetailProps) {
  const { essence, version, history, isLatest } = data;
  const source = getSource(version);
  const latest = essence.latestVersionNumber ?? version.versionNumber;
  const { session } = useSession();
  const role = (session?.user as { role?: string } | undefined)?.role;
  const isModerator = role === "admin" || role === "moderator";

  useTrackView(essence.id);

  return (
    <div className="mx-auto w-full max-w-[72ch]">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <EssenceStats essence={essence} />
        {history.length > 1 && onVersionChange && (
          <VersionSelector
            history={history}
            currentVersion={version.versionNumber}
            latestVersion={latest}
            onChange={onVersionChange}
            className="ml-auto"
          />
        )}
      </div>

      {!isLatest && onVersionChange && (
        <HistoricalNotice
          currentVersion={version.versionNumber}
          latestVersion={latest}
          onReturn={() => onVersionChange(latest)}
          className="mt-4"
        />
      )}

      <EssenceBody version={version} source={source} className="mt-7" />

      <div className="mt-10 border-t border-line pt-5">
        <CommunityTagsSection
          essenceId={essence.id}
          isAuthenticated={!!session}
          canModerate={isModerator}
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <FavoriteButton essenceId={essence.id} variant="label" />
        <CopyMarkdownButton version={version} source={source} />
        <FullPageLink essenceId={essence.id} query={browseQuery} className="ml-auto" />
      </div>
    </div>
  );
}
